import React, { Dispatch, FC, useReducer } from 'react'

import { ALPHABET } from 'utils/constants/alphabet'
import difference from 'lodash/difference'

export enum ActionType {
  SET_INDEX_LIST = 'SET_INDEX_LIST',
  SET_PENDING = 'SET_PENDING',
  SET_ACTIVE_INDEX = 'SET_ACTIVE_INDEX',
  SET_PAGING = 'SET_PAGING',
  RESET_INDEX_LIST = 'RESET_INDEX_LIST',
}

type ActionVal =
  | {
      type: ActionType.SET_INDEX_LIST
      payload: LooseObj
    }
  | {
      type: ActionType.SET_PENDING
    }
  | {
      type: ActionType.SET_ACTIVE_INDEX
      payload: string
    }
  | {
      type: ActionType.SET_PAGING
      payload: KampuspediaPaging
    }
  | {
      type: ActionType.RESET_INDEX_LIST
    }

export enum KampuspediaStatus {
  idle = 'idle',
  pending = 'pending',
  completed = 'completed',
  failed = 'failed',
}

export enum KampuspediaPaging {
  none = 'none',
  loadMore = 'loadMore',
  pagination = 'pagination',
}

export interface Term {
  title: string
  slug: string
}

export interface IndexList {
  indexDetailList: {
    index: string
    detailList?: Term[] | any[]
  }
  pageInfo?: {
    total: number
    hasNextPage: boolean
    page?: number
  }
}

export const indexListInit = {
  indexDetailList: {
    index: '',
    detailList: [],
  },
  pageInfo: {
    total: 0,
    page: 1,
    hasNextPage: false,
  },
}

interface KampuspediaState {
  indexList: IndexList
  allIndex: IndexList[]
  activeIndex: string
  disabledIndex: string[]
  paging: KampuspediaPaging
  status: KampuspediaStatus
}

export const initKampuspediaState = () => ({
  indexList: indexListInit,
  allIndex: [],
  activeIndex: '',
  disabledIndex: [],
  paging: KampuspediaPaging.none,
  status: KampuspediaStatus.idle,
})

const kampuspediaReducer = (state = initKampuspediaState(), action: ActionVal) => {
  switch (action.type) {
    case ActionType.SET_INDEX_LIST:
      return {
        ...state,
        indexList: action.payload.indexList,
        disabledIndex: action.payload.disabledIndex,
        status: KampuspediaStatus.completed,
        activeIndex: action.payload.activeIndex,
        allIndex: action.payload.allIndex || state.allIndex,
      }
    case ActionType.SET_PENDING:
      return { ...state, status: KampuspediaStatus.pending }
    case ActionType.SET_ACTIVE_INDEX:
      return { ...state, activeIndex: action.payload }
    case ActionType.SET_PAGING:
      return {
        ...state,
        paging: action.payload,
      }
    case ActionType.RESET_INDEX_LIST:
      return {
        ...state,
        indexListInit: indexListInit,
        paging: KampuspediaPaging.none,
      }
    default:
      return state
  }
}

const initialKampuspediaCtx: {
  state: KampuspediaState
  dispatch: Dispatch<ActionVal>
  setIndexList: (indexList: IndexList, allIndexData?: IndexList[]) => void
} = {
  state: initKampuspediaState(),
  dispatch: () => {
    return
  },
  setIndexList: () => {
    return
  },
}

export const KampuspediaCtx = React.createContext(initialKampuspediaCtx)

const KampuspediaProvider: FC = ({ children }) => {
  const [state, dispatch] = useReducer(kampuspediaReducer, initKampuspediaState())

  const setIndexList = (indexList: IndexList, allIndexData?: IndexList[]) => {
    dispatch({ type: ActionType.SET_PENDING })
    const allIndex = allIndexData || state.allIndex
    const allAvailableIndex = allIndex.map((item: IndexList) => item.indexDetailList.index)
    const diffIndex = difference(ALPHABET, allAvailableIndex)
    dispatch({
      type: ActionType.SET_INDEX_LIST,
      payload: {
        indexList: indexList,
        disabledIndex: diffIndex,
        activeIndex: indexList.indexDetailList.index,
        allIndex,
      },
    })
  }

  return <KampuspediaCtx.Provider value={{ state, dispatch, setIndexList }}>{children}</KampuspediaCtx.Provider>
}

export default KampuspediaProvider
